import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { ACADEMY } from "../../lib/constants";

const chapters = [
  { n: "01", title: "Rooted in Tradition", text: "Every journey begins with the ancient grammar of Indian classical dance — mudras, rhythm and devotion passed down through generations." },
  { n: "02", title: "Expressed in Motion", text: "From Bharatanatyam to hip hop, we honour each form's language while nurturing the individual artist within every student." },
  { n: "03", title: "Performed with Passion", text: "The stage is where discipline becomes art. We prepare our dancers for arangetrams, showcases and the joy of performance." },
];

export default function AboutPreview() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section className="relative bg-background py-24 md:py-32" data-testid="about-preview">
      <div className="rb-container grid lg:grid-cols-2 gap-16 lg:gap-24 items-start">
        {/* Left — image with parallax */}
        <div className="relative">
          <div className="img-zoom overflow-hidden">
            <motion.img
              style={{ y }}
              src="/dance-master.png"
              alt="Triveni Chowdary, Dance Master at Rythmbara Dance Academy"
              className="h-[520px] md:h-[680px] w-full object-cover object-[center_15%]"
            />
          </div>
          <Reveal delay={0.2} className="absolute -bottom-8 -right-4 md:right-8 bg-purple text-[#F6F4EE] p-8 max-w-xs shadow-2xl">
            <p className="font-serif text-2xl leading-tight">{ACADEMY.master}</p>
            <p className="overline text-gold mt-2 text-[10px]">Founder &amp; Dance Master</p>
          </Reveal>
        </div>

        {/* Right — manifesto chapters */}
        <div>
          <Reveal>
            <p className="overline text-accent-foreground/60 mb-5 flex items-center gap-3">
              <span className="h-px w-10 bg-[var(--rb-gold)] inline-block" /> About the Academy
            </p>
            <h2 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-foreground">
              A sanctuary where <span className="italic gold-gradient-text">rhythm</span> becomes a way of life.
            </h2>
          </Reveal>

          <div className="mt-12 space-y-10">
            {chapters.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.12}>
                <div className="flex gap-6 border-t border-border pt-6">
                  <span className="font-serif text-3xl text-[var(--rb-gold)] leading-none">{c.n}</span>
                  <div>
                    <h3 className="font-serif text-2xl text-foreground mb-2">{c.title}</h3>
                    <p className="text-muted-foreground leading-relaxed max-w-md">{c.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <Link to="/about" data-testid="about-more-btn" className="group mt-12 inline-flex items-center gap-2 text-foreground font-medium border-b-2 border-[var(--rb-gold)] pb-1 hover:gap-4 transition-all duration-300">
              Discover our story <ArrowUpRight className="h-5 w-5 text-[var(--rb-gold)]" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
